import { useMemo, useState } from 'react';
import { AlertTriangle, Check, ChevronDown, ChevronUp, Loader2, Plus, RotateCcw, Trash2, X } from 'lucide-react';
import {
    useCreateCheckAddonMutation,
    useDeleteCheckAddonMutation,
    useGetAdminPlansQuery,
    useGetCheckAddonsQuery,
    useReorderCheckAddonsMutation,
    useReseedCheckAddonsMutation,
    useUpdateCheckAddonMutation,
} from '@/store/api/apiSlice';
import { parseErrorMessage } from '@/utils/parseErrorMessage';
import type { AdminCheckAddon, CreateCheckAddonRequest } from '@/types';

function formatNaira(value: number) {
    return new Intl.NumberFormat('en-NG', { style: 'currency', currency: 'NGN', minimumFractionDigits: 0 }).format(
        value || 0,
    );
}

// Turn a display name into the slug the API wants, so an admin never has to
// think about the id format. Editable before saving.
function slugify(name: string) {
    return name
        .toLowerCase()
        .replace(/[^a-z0-9]+/g, '_')
        .replace(/^_+|_+$/g, '')
        .slice(0, 40);
}

type PlanOption = { id: string; name: string };

/**
 * Super-admin catalog manager. Add-ons are DB-backed: create them, set the
 * amount, choose which tiers sell them, reorder, hide, delete. Every change is
 * live immediately — the pricing cards and checkout read the same catalog.
 */
export default function CheckAddonsManager() {
    const { data: addons, isLoading, error: loadError } = useGetCheckAddonsQuery();
    const { data: plans } = useGetAdminPlansQuery();
    const [createAddon, { isLoading: isCreating }] = useCreateCheckAddonMutation();
    const [reorderAddons] = useReorderCheckAddonsMutation();
    const [reseedAddons, { isLoading: isReseeding }] = useReseedCheckAddonsMutation();

    const [isAdding, setIsAdding] = useState(false);
    const [error, setError] = useState('');
    const [success, setSuccess] = useState('');

    // Tier checkboxes come from the live plan list, so a new plan shows up here
    // without a code change.
    const planOptions: PlanOption[] = useMemo(() => (plans ?? []).map((p) => ({ id: p.id, name: p.name })), [plans]);

    const ordered = useMemo(() => [...(addons ?? [])].sort((a, b) => a.position - b.position), [addons]);

    const handleCreate = async (draft: CreateCheckAddonRequest) => {
        setError('');
        setSuccess('');
        try {
            await createAddon(draft).unwrap();
            setIsAdding(false);
            setSuccess(`"${draft.name}" is live on the pricing cards.`);
        } catch (err) {
            setError(parseErrorMessage(err, 'Failed to create the add-on.'));
        }
    };

    const handleMove = async (index: number, direction: -1 | 1) => {
        const target = index + direction;
        if (target < 0 || target >= ordered.length) return;
        const ids = ordered.map((a) => a.id);
        const moved = ids[index];
        const displaced = ids[target];
        if (moved === undefined || displaced === undefined) return;
        ids[index] = displaced;
        ids[target] = moved;
        setError('');
        try {
            await reorderAddons(ids).unwrap();
        } catch (err) {
            setError(parseErrorMessage(err, 'Failed to reorder add-ons.'));
        }
    };

    const handleReseed = async () => {
        setError('');
        setSuccess('');
        try {
            await reseedAddons().unwrap();
            setSuccess('Built-in add-ons restored to their default name, price and tiers.');
        } catch (err) {
            setError(parseErrorMessage(err, 'Failed to restore defaults.'));
        }
    };

    return (
        <div className="rounded-2xl border border-gray-100 p-5 flex flex-col gap-4">
            <div className="flex flex-col sm:flex-row sm:items-start justify-between gap-3">
                <div>
                    <h3 className="text-sm font-bold text-gray-900">Check add-ons</h3>
                    <p className="text-xs text-gray-500 mt-0.5">
                        Extras sold on top of a plan. An add-on left off a tier is one that tier already includes.
                        Changes go live immediately.
                    </p>
                </div>
                <div className="flex items-center gap-2 shrink-0">
                    <button
                        type="button"
                        onClick={() => setIsAdding((v) => !v)}
                        className="bg-[#FF6B35] hover:bg-[#FF5722] text-white px-4 py-2 rounded-xl text-xs font-medium flex items-center gap-1.5"
                    >
                        {isAdding ? <X className="w-3.5 h-3.5" /> : <Plus className="w-3.5 h-3.5" />}
                        {isAdding ? 'Cancel' : 'New add-on'}
                    </button>
                    <button
                        type="button"
                        onClick={handleReseed}
                        disabled={isReseeding}
                        className="border border-gray-200 hover:bg-gray-50 disabled:opacity-50 text-gray-700 px-3 py-2 rounded-xl text-xs font-medium flex items-center gap-1.5"
                    >
                        {isReseeding ? (
                            <Loader2 className="w-3.5 h-3.5 animate-spin" />
                        ) : (
                            <RotateCcw className="w-3.5 h-3.5" />
                        )}
                        Defaults
                    </button>
                </div>
            </div>

            {error && (
                <div className="flex items-start gap-2 rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
                    <AlertTriangle className="w-4 h-4 shrink-0 mt-0.5" />
                    {error}
                </div>
            )}
            {success && (
                <div className="flex items-center gap-2 rounded-xl border border-green-200 bg-green-50 px-4 py-3 text-sm text-green-700">
                    <Check className="w-4 h-4 shrink-0" />
                    {success}
                </div>
            )}

            {isAdding && <AddonForm planOptions={planOptions} isSaving={isCreating} onSubmit={handleCreate} />}

            {isLoading ? (
                <div className="flex items-center justify-center py-10">
                    <Loader2 className="h-6 w-6 text-[#FF6B35] animate-spin" />
                </div>
            ) : loadError ? (
                <div className="flex items-center gap-2 rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
                    <AlertTriangle className="w-4 h-4 shrink-0" />
                    Failed to load the add-on catalog.
                </div>
            ) : ordered.length === 0 ? (
                <p className="text-sm text-gray-500 py-6 text-center">
                    No add-ons yet. Create one, or restore the built-in defaults.
                </p>
            ) : (
                <div className="flex flex-col gap-2">
                    {ordered.map((addon, index) => (
                        <AddonRow
                            key={addon.id}
                            addon={addon}
                            planOptions={planOptions}
                            isFirst={index === 0}
                            isLast={index === ordered.length - 1}
                            onMove={(direction) => handleMove(index, direction)}
                            onError={setError}
                            onSuccess={setSuccess}
                        />
                    ))}
                </div>
            )}
        </div>
    );
}

function AddonRow({
    addon,
    planOptions,
    isFirst,
    isLast,
    onMove,
    onError,
    onSuccess,
}: {
    addon: AdminCheckAddon;
    planOptions: PlanOption[];
    isFirst: boolean;
    isLast: boolean;
    onMove: (direction: -1 | 1) => void;
    onError: (message: string) => void;
    onSuccess: (message: string) => void;
}) {
    const [updateAddon, { isLoading: isSaving }] = useUpdateCheckAddonMutation();
    const [deleteAddon, { isLoading: isDeleting }] = useDeleteCheckAddonMutation();
    const [isEditing, setIsEditing] = useState(false);

    // Which tiers sell it. Empty/null on the server means "every tier".
    const soldOn = addon.availableFor?.length
        ? planOptions.filter((p) => addon.availableFor?.includes(p.id)).map((p) => p.name)
        : ['All tiers'];

    const handleToggleActive = async () => {
        onError('');
        try {
            await updateAddon({ id: addon.id, data: { isActive: !addon.isActive } }).unwrap();
            onSuccess(`"${addon.name}" ${addon.isActive ? 'hidden from checkout' : 'is back on the pricing cards'}.`);
        } catch (err) {
            onError(parseErrorMessage(err, 'Failed to update the add-on.'));
        }
    };

    const handleDelete = async () => {
        onError('');
        onSuccess('');
        try {
            await deleteAddon(addon.id).unwrap();
            onSuccess(`"${addon.name}" deleted.`);
        } catch (err) {
            // The API refuses once an investigation has been charged for it.
            onError(parseErrorMessage(err, 'Failed to delete the add-on.'));
        }
    };

    if (isEditing) {
        return (
            <AddonForm
                planOptions={planOptions}
                existing={addon}
                isSaving={isSaving}
                onCancel={() => setIsEditing(false)}
                onSubmit={async (draft) => {
                    onError('');
                    try {
                        const { id: _id, ...data } = draft;
                        await updateAddon({ id: addon.id, data }).unwrap();
                        setIsEditing(false);
                        onSuccess(`"${draft.name}" updated. New checkouts use it immediately.`);
                    } catch (err) {
                        onError(parseErrorMessage(err, 'Failed to update the add-on.'));
                    }
                }}
            />
        );
    }

    return (
        <div
            className={`flex items-center gap-3 rounded-xl border px-3 py-2.5 ${
                addon.isActive ? 'border-gray-100' : 'border-gray-100 bg-gray-50 opacity-70'
            }`}
        >
            <div className="flex flex-col gap-0.5 shrink-0">
                <button
                    type="button"
                    onClick={() => onMove(-1)}
                    disabled={isFirst}
                    className="text-gray-300 hover:text-gray-600 disabled:opacity-30 disabled:hover:text-gray-300"
                    aria-label="Move up"
                >
                    <ChevronUp className="w-3.5 h-3.5" />
                </button>
                <button
                    type="button"
                    onClick={() => onMove(1)}
                    disabled={isLast}
                    className="text-gray-300 hover:text-gray-600 disabled:opacity-30 disabled:hover:text-gray-300"
                    aria-label="Move down"
                >
                    <ChevronDown className="w-3.5 h-3.5" />
                </button>
            </div>

            <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                    <span className="text-sm font-semibold text-gray-900 truncate">{addon.name}</span>
                    <span className="text-sm font-bold text-[#FF6B35]">{formatNaira(addon.price)}</span>
                    {!addon.isActive && (
                        <span className="rounded-full bg-gray-200 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wider text-gray-600">
                            Hidden
                        </span>
                    )}
                </div>
                <p className="text-[11px] text-gray-400 mt-0.5 truncate">
                    {addon.id} · {soldOn.join(', ')}
                </p>
            </div>

            <div className="flex items-center gap-1.5 shrink-0">
                <button
                    type="button"
                    onClick={() => setIsEditing(true)}
                    className="border border-gray-200 hover:bg-gray-50 text-gray-700 px-3 py-1.5 rounded-lg text-xs font-medium"
                >
                    Edit
                </button>
                <button
                    type="button"
                    onClick={handleToggleActive}
                    disabled={isSaving}
                    className="border border-gray-200 hover:bg-gray-50 disabled:opacity-50 text-gray-700 px-3 py-1.5 rounded-lg text-xs font-medium"
                >
                    {addon.isActive ? 'Hide' : 'Show'}
                </button>
                <button
                    type="button"
                    onClick={handleDelete}
                    disabled={isDeleting}
                    className="text-gray-300 hover:text-red-500 disabled:opacity-50 p-1.5"
                    aria-label={`Delete ${addon.name}`}
                >
                    {isDeleting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Trash2 className="w-4 h-4" />}
                </button>
            </div>
        </div>
    );
}

function AddonForm({
    planOptions,
    existing,
    isSaving,
    onSubmit,
    onCancel,
}: {
    planOptions: PlanOption[];
    existing?: AdminCheckAddon;
    isSaving: boolean;
    onSubmit: (draft: CreateCheckAddonRequest) => void | Promise<void>;
    onCancel?: () => void;
}) {
    const [name, setName] = useState(existing?.name ?? '');
    // The id is the stable checkout key, so it is set once and never edited.
    const [id, setId] = useState(existing?.id ?? '');
    const [idTouched, setIdTouched] = useState(Boolean(existing));
    const [description, setDescription] = useState(existing?.description ?? '');
    const [price, setPrice] = useState(existing ? String(existing.price) : '');
    const [allTiers, setAllTiers] = useState(!existing?.availableFor?.length);
    const [tiers, setTiers] = useState<string[]>(existing?.availableFor ?? []);

    const priceNum = Number(price);
    const priceValid = price.trim() !== '' && Number.isInteger(priceNum) && priceNum >= 0;
    const canSave = name.trim() !== '' && id.trim() !== '' && priceValid && (allTiers || tiers.length > 0);

    const handleName = (value: string) => {
        setName(value);
        // Keep the slug in step with the name until the admin edits it directly.
        if (!idTouched) setId(slugify(value));
    };

    const toggleTier = (planId: string) =>
        setTiers((prev) => (prev.includes(planId) ? prev.filter((t) => t !== planId) : [...prev, planId]));

    return (
        <div className="rounded-xl border border-[#FF6B35]/30 bg-orange-50/40 p-4 flex flex-col gap-3">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <Field label="Name">
                    <input
                        type="text"
                        value={name}
                        onChange={(e) => handleName(e.target.value)}
                        placeholder="Drug Screening"
                        className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-[#FF6B35]"
                    />
                </Field>
                <Field label="Price" hint={priceValid ? formatNaira(priceNum) : 'Whole naira, no kobo'}>
                    <div className="relative">
                        <span className="absolute left-3 top-1/2 -translate-y-1/2 text-sm text-gray-400">₦</span>
                        <input
                            type="text"
                            inputMode="numeric"
                            value={price}
                            onChange={(e) => setPrice(e.target.value.replace(/[^\d]/g, ''))}
                            className="w-full border border-gray-200 rounded-lg pl-7 pr-3 py-2 text-sm focus:outline-none focus:border-[#FF6B35]"
                        />
                    </div>
                </Field>
            </div>

            <Field label="Description" hint="Shown under the add-on on the pricing card">
                <textarea
                    value={description}
                    onChange={(e) => setDescription(e.target.value)}
                    rows={2}
                    placeholder="What this add-on covers."
                    className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-[#FF6B35] resize-none"
                />
            </Field>

            {!existing && (
                <Field label="Id" hint="The checkout key — lower-case, no spaces. Cannot be changed later.">
                    <input
                        type="text"
                        value={id}
                        onChange={(e) => {
                            setIdTouched(true);
                            setId(e.target.value.toLowerCase().replace(/[^a-z0-9_]/g, ''));
                        }}
                        className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm font-mono focus:outline-none focus:border-[#FF6B35]"
                    />
                </Field>
            )}

            <div className="flex flex-col gap-1.5">
                <span className="text-sm font-medium text-gray-800">Sold on</span>
                <span className="text-[11px] text-gray-400 -mt-1">
                    Leave a tier off when it already includes this check, so it is never sold twice.
                </span>
                <div className="flex flex-wrap gap-2 mt-1">
                    <TierChip label="All tiers" checked={allTiers} onClick={() => setAllTiers(true)} />
                    {planOptions.map((plan) => (
                        <TierChip
                            key={plan.id}
                            label={plan.name}
                            checked={!allTiers && tiers.includes(plan.id)}
                            onClick={() => {
                                setAllTiers(false);
                                toggleTier(plan.id);
                            }}
                        />
                    ))}
                </div>
            </div>

            <div className="flex items-center gap-2">
                <button
                    type="button"
                    disabled={!canSave || isSaving}
                    onClick={() =>
                        onSubmit({
                            id: id.trim(),
                            name: name.trim(),
                            description: description.trim(),
                            price: priceNum,
                            availableFor: allTiers ? null : tiers,
                        })
                    }
                    className="bg-[#FF6B35] hover:bg-[#FF5722] disabled:opacity-50 disabled:cursor-not-allowed text-white px-5 py-2 rounded-xl text-sm font-medium flex items-center gap-2"
                >
                    {isSaving && <Loader2 className="w-4 h-4 animate-spin" />}
                    {existing ? 'Save changes' : 'Create add-on'}
                </button>
                {onCancel && (
                    <button
                        type="button"
                        onClick={onCancel}
                        className="border border-gray-200 hover:bg-gray-50 text-gray-700 px-4 py-2 rounded-xl text-sm font-medium"
                    >
                        Cancel
                    </button>
                )}
            </div>
        </div>
    );
}

function Field({ label, hint, children }: { label: string; hint?: string; children: React.ReactNode }) {
    return (
        <div className="flex flex-col gap-1.5">
            <label className="text-sm font-medium text-gray-800">{label}</label>
            {children}
            {hint && <span className="text-[11px] text-gray-400">{hint}</span>}
        </div>
    );
}

function TierChip({ label, checked, onClick }: { label: string; checked: boolean; onClick: () => void }) {
    return (
        <button
            type="button"
            onClick={onClick}
            className={`rounded-full px-3 py-1.5 text-xs font-medium border transition-colors ${
                checked
                    ? 'border-[#FF6B35] bg-[#FF6B35] text-white'
                    : 'border-gray-200 bg-white text-gray-600 hover:border-gray-300'
            }`}
        >
            {label}
        </button>
    );
}
